import React, { useEffect, useRef, useCallback } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  Keyboard,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  Image,
  SafeAreaView,
  NativeSyntheticEvent,
  TextInputKeyPressEventData,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { router, useLocalSearchParams, useFocusEffect } from "expo-router";
import { useQuery } from "@tanstack/react-query";
import { FontFamilies } from "@/constants/Typography";
import { Colors } from "@/constants/Colors";
import { useChat, ChatMessage } from "@/hooks/useChat";
import {
  AudioPlayerWidget,
  ActiveCallWaveform,
} from "@/components/chat/ChatComponents";
import RefundProcessServices from "@/services/refundProcessServices";
import { Shadows } from "@/constants/Shadows";
import { SCREEN_WIDTH } from "@/constants/Sizes";
import FBTrackingService from "@/services/fbTrackingService";
import { StyleSheet } from "@/utils/appStyleSheet";

// Parse text with **bold** markdown syntax
const parseMessageText = (
  text: string,
  baseStyle: object,
  boldStyle: object
): React.ReactNode[] => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      const boldText = part.slice(2, -2);
      return (
        <Text key={index} style={[baseStyle, boldStyle]}>
          {boldText}
        </Text>
      );
    }
    return part;
  });
};

// Suggestion prompts from Figma design
const SUGGESTION_PROMPTS = [
  "Cancel my Netflix subscription",
  "Negotiate my hotel booking because I found a lower price elsewhere",
  "Request a discount on my Target order",
];

export default function ChatScreen() {
  const insets = useSafeAreaInsets();
  const scrollViewRef = useRef<ScrollView>(null);

  // Get chatSessionId from route params (used when returning from linked-emails)
  const { chatSessionId: initialSessionId } = useLocalSearchParams<{
    chatSessionId?: string;
  }>();

  useFocusEffect(
    React.useCallback(() => {
      FBTrackingService.trackViewContent("sparrow-ai-chat");
    }, [])
  );

  // Check if user has linked emails
  const { data: linkedEmails } = useQuery({
    queryKey: ["linkedEmails"],
    queryFn: RefundProcessServices.getLinkedEmail,
  });

  const hasLinkedEmail = linkedEmails && linkedEmails.length > 0;

  const {
    inputText,
    setInputText,
    messages,
    chatSessionId,
    isLoading,
    isInitializing,
    isPollingCall,
    isAITyping,
    isAudioConnected,
    isAudioPlaying,
    inputRef,
    initializeSession,
    sendMessage,
    cleanup,
  } = useChat({ initialSessionId: initialSessionId || undefined });

  // Initialize session when screen mounts
  useEffect(() => {
    initializeSession();
  }, [initializeSession]);

  // Scroll to bottom when messages change (including streaming updates)
  const lastMessage = messages[messages.length - 1];
  useEffect(() => {
    if (messages.length > 0) {
      setTimeout(() => {
        scrollViewRef.current?.scrollToEnd({ animated: true });
      }, 100);
    }
  }, [messages.length, lastMessage?.text]);

  const handleClose = () => {
    Keyboard.dismiss();
    cleanup();
    router.dismissTo("/(tabs)/feeds");
  };

  const handleSuggestionPress = (suggestion: string) => {
    setInputText(suggestion);
    // Use setTimeout to ensure state is updated before sending
    setTimeout(() => {
      sendMessage();
      scrollViewRef.current?.scrollToEnd({ animated: true });
    }, 100);
  };

  const handleSend = () => {
    sendMessage();
    // Scroll to bottom after sending
    setTimeout(() => {
      scrollViewRef.current?.scrollToEnd({ animated: true });
    }, 100);
  };

  // Handle key press for Enter to send, Ctrl/Shift+Enter for newline
  const handleKeyPress = useCallback(
    (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
      if (Platform.OS === "web") {
        const nativeEvent = e.nativeEvent as TextInputKeyPressEventData & {
          ctrlKey?: boolean;
          shiftKey?: boolean;
          metaKey?: boolean;
        };
        if (nativeEvent.key === "Enter") {
          if (
            nativeEvent.ctrlKey ||
            nativeEvent.shiftKey ||
            nativeEvent.metaKey
          ) {
            // Allow newline - don't prevent default
            return;
          }
          // Send message on plain Enter
          e.preventDefault();
          if (inputText.trim() && !isLoading && !isInitializing) {
            handleSend();
          }
        }
      }
    },
    [inputText, isLoading, isInitializing, handleSend]
  );

  const handleLinkEmail = () => {
    router.push({
      pathname: "/account/linked-emails",
      params: {
        returnTo: "chat",
        chatSessionId: chatSessionId || "",
      },
    });
  };

  // Check if message should show link email button
  const shouldShowLinkEmail = (message: ChatMessage): boolean => {
    if (message.isUser) return false;
    return message.action === "link_email";
  };

  const renderMessage = (message: ChatMessage) => {
    // Render audio player widget for call recording
    if (message.type === "call_audio" && message.audioUrl) {
      return (
        <AudioPlayerWidget
          key={message.id}
          audioUrl={message.audioUrl}
          duration={message.audioDuration}
        />
      );
    }

    // Check if we should show link email button
    const showLinkEmail = shouldShowLinkEmail(message);

    // Render message with optional link email action
    if (showLinkEmail) {
      return (
        <View
          key={message.id}
          style={[styles.messageBubble, styles.botMessage]}
        >
          <Text style={[styles.messageText, styles.botMessageText]}>
            {parseMessageText(
              message.text,
              styles.botMessageText,
              styles.boldText
            )}
          </Text>
          <TouchableOpacity onPress={handleLinkEmail} style={styles.actionLink}>
            <Text style={styles.actionLinkText}>Link Gmail account</Text>
            <Ionicons name="chevron-forward" size={16} color="#FFFFFF" />
          </TouchableOpacity>
        </View>
      );
    }

    // Render regular message bubble
    const textStyle = message.isUser
      ? styles.messageText
      : [styles.messageText, styles.botMessageText];

    return (
      <View
        key={message.id}
        style={[
          styles.messageBubble,
          message.isUser ? styles.userMessage : styles.botMessage,
        ]}
      >
        <Text style={textStyle}>
          {parseMessageText(
            message.text,
            message.isUser ? styles.messageText : styles.botMessageText,
            styles.boldText
          )}
        </Text>
      </View>
    );
  };

  const hasMessages = messages.length > 0;

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerContent}>
          <View style={styles.headerLeft}>
            <Image
              source={require("@/assets/icons/claim-chat.png")}
              style={styles.avatarImage}
              resizeMode="cover"
            />
            <View style={styles.headerTextContainer}>
              <Text style={styles.headerTitle}>Sparrow AI</Text>
              {hasLinkedEmail && (
                <View style={styles.statusContainer}>
                  <View style={styles.statusDot} />
                  <Text style={styles.statusText}>
                    Connected to email inbox
                  </Text>
                </View>
              )}
            </View>
          </View>
          <TouchableOpacity
            style={styles.closeButton}
            onPress={handleClose}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <Ionicons name="close" size={24} color="#161D14" />
          </TouchableOpacity>
        </View>
      </View>

      {/* Main Content */}
      <KeyboardAvoidingView
        style={styles.keyboardAvoidingView}
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        keyboardVerticalOffset={0}
      >
        <View style={styles.contentContainer}>
          {/* Messages Area */}
          <ScrollView
            ref={scrollViewRef}
            style={styles.messagesContainer}
            contentContainerStyle={styles.messagesContent}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            {messages.map(renderMessage)}
            {(isLoading || isAITyping) && (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="small" color={Colors.primary} />
              </View>
            )}
          </ScrollView>

          {/* Suggestion Chips - Only show when no messages */}
          {!hasMessages && !isPollingCall && (
            <View style={styles.suggestionsContainer}>
              {SUGGESTION_PROMPTS.map((suggestion, index) => (
                <TouchableOpacity
                  key={index}
                  style={styles.suggestionChip}
                  onPress={() => handleSuggestionPress(suggestion)}
                  activeOpacity={0.7}
                >
                  <Text style={styles.suggestionText}>{suggestion}</Text>
                </TouchableOpacity>
              ))}
            </View>
          )}

          {/* Input Area - Show waveform when calling, text input otherwise */}
          <View
            style={[styles.inputWrapper, { paddingBottom: insets.bottom + 16 }]}
          >
            {isPollingCall ? (
              <ActiveCallWaveform
                isStreaming={isAudioConnected || isAudioPlaying}
              />
            ) : (
              <View style={styles.inputContainer}>
                <TextInput
                  ref={inputRef}
                  style={styles.textInput}
                  placeholder="Ask anything..."
                  placeholderTextColor="#A3A3A3"
                  value={inputText}
                  onChangeText={setInputText}
                  maxLength={500}
                  returnKeyType="send"
                  blurOnSubmit={false}
                  onSubmitEditing={handleSend}
                  onKeyPress={handleKeyPress}
                  editable={!isInitializing && !isLoading}
                  multiline={true}
                  selectionColor={Colors.primary}
                  underlineColorAndroid="transparent"
                />
                <TouchableOpacity
                  style={[
                    styles.sendButton,
                    inputText.trim() && !isLoading
                      ? styles.sendButtonActive
                      : styles.sendButtonInactive,
                  ]}
                  onPress={handleSend}
                  disabled={!inputText.trim() || isLoading || isInitializing}
                >
                  {isLoading ? (
                    <ActivityIndicator size="small" color="#FFFFFF" />
                  ) : (
                    <Ionicons
                      name="arrow-up"
                      size={20}
                      color={inputText.trim() ? "#FFFFFF" : "#00000040"}
                    />
                  )}
                </TouchableOpacity>
              </View>
            )}
          </View>
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    width: "100%",
    maxWidth: SCREEN_WIDTH,
    alignSelf: "center",
  },
  header: {
    borderBottomWidth: 1,
    borderBottomColor: "#EBEBEB",
  },
  headerContent: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  avatarImage: {
    width: 32,
    height: 32,
    borderRadius: 16,
  },
  headerTextContainer: {
    gap: 2,
  },
  headerTitle: {
    fontSize: 16,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#161D14",
    lineHeight: 23,
  },
  statusContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  statusDot: {
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: Colors.primary,
  },
  statusText: {
    fontSize: 12,
    fontFamily: FontFamilies.Inter.Medium,
    color: Colors.primary,
    lineHeight: 23,
  },
  closeButton: {
    width: 32,
    height: 32,
    alignItems: "center",
    justifyContent: "center",
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  contentContainer: {
    flex: 1,
    backgroundColor: "#F9F9F9",
  },
  messagesContainer: {
    flex: 1,
  },
  messagesContent: {
    flexGrow: 1,
    paddingHorizontal: 16,
    paddingTop: 16,
    gap: 8,
  },
  loadingContainer: {
    paddingVertical: 12,
    alignItems: "flex-start",
  },
  messageBubble: {
    maxWidth: "85%",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    flexShrink: 1,
  },
  userMessage: {
    alignSelf: "flex-end",
    backgroundColor: "#FFFFFF",
  },
  botMessage: {
    backgroundColor: Colors.primary,
    alignSelf: "flex-start",
  },
  messageText: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#161D14",
    lineHeight: 23,
    flexShrink: 1,
    flexWrap: "wrap",
  },
  botMessageText: {
    color: "#FFFFFF",
  },
  boldText: {
    fontFamily: FontFamilies.Inter.SemiBold,
  },
  actionLink: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: "rgba(255, 255, 255, 0.3)",
  },
  actionLinkText: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#FFFFFF",
    flex: 1,
  },
  suggestionsContainer: {
    position: "absolute",
    left: 16,
    right: 16,
    bottom: 85,
    gap: 8,
  },
  suggestionChip: {
    backgroundColor: "#F2FFF5",
    borderWidth: 1,
    borderColor: "rgba(15, 145, 41, 0.2)",
    borderRadius: 8,
    paddingVertical: 16,
    paddingHorizontal: 8,
    alignItems: "center",
  },
  suggestionText: {
    fontSize: 13,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#0E3115",
    textAlign: "center",
    lineHeight: 17,
    letterSpacing: -0.26,
  },
  inputWrapper: {
    paddingHorizontal: 16,
    paddingTop: 12,
  },
  inputContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    gap: 8,
    minHeight: 56,
    ...Shadows.light,
  },
  textInput: {
    flex: 1,
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#161D14",
    maxHeight: 120,
    paddingVertical: 8,
    outlineStyle: "none",
  },
  sendButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
  },
  sendButtonActive: {
    backgroundColor: Colors.primary,
  },
  sendButtonInactive: {
    backgroundColor: "#E5E5E5",
  },
});
